/**
 * Hero Slideshow Web Component
 * Auto-advancing slideshow with interactive dots
 * Features:
 * - Configurable autoplay (enable/disable)
 * - Configurable slide duration
 * - Progress bar animation on active dot
 * - Keyboard navigation support
 * - Respects prefers-reduced-motion
 */

if (!customElements.get('hero-slideshow')) {
    class HeroSlideshow extends HTMLElement {
        constructor() {
            super();
            this.slides = [];
            this.dots = [];
            this.currentIndex = 0;
            this.totalSlides = 0;
            this.slideInterval = null;
            this.isAutoplay = true;
            this.duration = 7000;
            this.prefersReducedMotion = false;
        }

        connectedCallback() {
            this.slides = Array.from(this.querySelectorAll('[data-slide-index]'));
            this.dots = Array.from(this.querySelectorAll('[data-dot-index]'));
            this.totalSlides = this.slides.length;

            if (this.totalSlides === 0) return;

            // Get settings from data attributes
            this.isAutoplay = this.dataset.autoplay === 'true';
            this.duration = parseInt(this.dataset.duration, 10) || 7000;

            // Check for reduced motion preference
            this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            this.init();
        }

        disconnectedCallback() {
            this.stopAutoplay();
            this.removeEventListeners();
        }

        init() {
            // Set up dot click handlers
            this.dots.forEach((dot, index) => {
                dot.addEventListener('click', () => this.handleDotClick(index));
            });

            // Keyboard navigation
            this.addEventListener('keydown', this.handleKeydown.bind(this));

            // Pause on hover/focus for accessibility
            this.addEventListener('mouseenter', () => this.pauseAutoplay());
            this.addEventListener('mouseleave', () => this.resumeAutoplay());
            this.addEventListener('focusin', () => this.pauseAutoplay());
            this.addEventListener('focusout', (e) => {
                if (!this.contains(e.relatedTarget)) {
                    this.resumeAutoplay();
                }
            });

            // Listen for reduced motion changes
            window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
                this.prefersReducedMotion = e.matches;
                if (this.prefersReducedMotion) {
                    this.stopAutoplay();
                } else if (this.isAutoplay) {
                    this.startAutoplay();
                }
            });

            // Start autoplay if enabled
            if (this.isAutoplay && !this.prefersReducedMotion) {
                this.startAutoplay();
            }
        }

        removeEventListeners() {
            this.dots.forEach((dot) => {
                dot.removeEventListener('click', this.handleDotClick);
            });
            this.removeEventListener('keydown', this.handleKeydown);
        }

        goToSlide(index) {
            if (index === this.currentIndex || index < 0 || index >= this.totalSlides) return;

            // Deactivate current slide and dot
            this.slides[this.currentIndex].classList.remove('hero-slideshow__slide--active');
            this.dots[this.currentIndex].classList.remove('hero-slideshow__dot--active');

            // Update index
            this.currentIndex = index;

            // Activate new slide and dot
            this.slides[this.currentIndex].classList.add('hero-slideshow__slide--active');
            this.dots[this.currentIndex].classList.add('hero-slideshow__dot--active');
        }

        nextSlide() {
            const nextIndex = (this.currentIndex + 1) % this.totalSlides;
            this.goToSlide(nextIndex);
        }

        prevSlide() {
            const prevIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
            this.goToSlide(prevIndex);
        }

        handleDotClick(index) {
            if (index !== this.currentIndex) {
                this.goToSlide(index);
                // Restart autoplay timer after manual interaction
                if (this.isAutoplay && !this.prefersReducedMotion) {
                    this.restartAutoplay();
                }
            }
        }

        handleKeydown(e) {
            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    this.prevSlide();
                    if (this.isAutoplay && !this.prefersReducedMotion) {
                        this.restartAutoplay();
                    }
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    this.nextSlide();
                    if (this.isAutoplay && !this.prefersReducedMotion) {
                        this.restartAutoplay();
                    }
                    break;
            }
        }

        startAutoplay() {
            if (this.slideInterval) return;
            this.slideInterval = setInterval(() => this.nextSlide(), this.duration);
        }

        stopAutoplay() {
            if (this.slideInterval) {
                clearInterval(this.slideInterval);
                this.slideInterval = null;
            }
        }

        restartAutoplay() {
            this.stopAutoplay();
            this.startAutoplay();
        }

        pauseAutoplay() {
            if (this.slideInterval) {
                this.stopAutoplay();
                // Add paused class to stop CSS animation
                this.dots.forEach(dot => dot.classList.add('hero-slideshow__dot--paused'));
            }
        }

        resumeAutoplay() {
            if (this.isAutoplay && !this.prefersReducedMotion) {
                // Remove paused class to resume CSS animation
                this.dots.forEach(dot => dot.classList.remove('hero-slideshow__dot--paused'));
                this.startAutoplay();
            }
        }
    }

    customElements.define('hero-slideshow', HeroSlideshow);
}

